import {
  isDevelopmentEnv,
  isNumeric,
  GUEST_TYPE,
  OAPI_API_TYPE,
  ROUTE_NAMES,
  ERROR_PICK_UP_TIME_NO_LONGER_AVAILABLE,
  SENTRY_ERRORS,
  ERROR_VENDOR_SELECTED_NOT_AVAILABLE
} from "../utils";
import { DateTime } from "luxon";
import { random } from "lodash";
import errorLoggerAdapter from "@/adapters/errorLoggerAdapter";
import { useOrderAheadMapper } from "@/store/modules/orderAhead/mapper";

const { mapToEndTime } = useOrderAheadMapper();

const store = Symbol("store");
const pastMessage = "Pickup time has passed, please select another time.";
export class RouteGuards {
  constructor() {
    this[store] = null;
    this.fetchVenueWasCalled = false;
  }

  wrapStore(storeInstance) {
    this[store] = storeInstance;
  }

  async fetchVenue(venue) {
    const venueId = this[store].getters["venue/getActiveVenueByUrl"](venue);

    //Only clean data when IW was mounted the first time
    if (!this.fetchVenueWasCalled) {
      this.fetchVenueWasCalled = true;

      //We will always clean the spinner state to avoid inconsistencies that may leave IW unusable
      this[store].commit("spinner/resetState", null, { root: true });

      await this[store].dispatch("common/cleanExpiredData", venueId);
    }

    /* 
    Check if venue being fetched is not active 
    and also if there was already a venue in app state
    */
    if (
      !this[store].getters["venueSettings/isActiveVenue"](venueId) &&
      this[store].getters["venueSettings/getVenueSettings"].venueId
    ) {
      //Call action to reset app state for all modules
      this[store].dispatch("common/resetAppState", null, { root: true });
      if (isDevelopmentEnv) console.log("Application state was reset");
    }

    await this[store].dispatch("venueSettings/fetchInteractWebSettings", venueId);
    const fetchActiveVenue = await this[store].dispatch("venue/fetchActiveVenue", venue);

    // Note: As this endpoint is not yet available, this won't consume any time.
    // When the backend endpoint is ready we should decide if wait for it, wait certain time or just show the default Theme until the request is completed.
    await this[store].dispatch("theme/fetchVenueTheme", venueId);

    return fetchActiveVenue;
  }

  getRedirectPath(params) {
    const seatRequired = this[store].getters["venueSettings/seatRequired"];

    return seatRequired && !this[store].getters["vendor/getVendorsList"].length ? `/${params.venue}` : undefined;
  }

  userRouteGuard = async (to, from, next) => {
    let isUserAuthorized = false;
    try {
      isUserAuthorized = await this[store].dispatch("user/checkUserIsAuthorized");
    } catch (e) {
      isUserAuthorized = false;
    }

    if (!isUserAuthorized || this[store].getters["user/getUserType"] === GUEST_TYPE) {
      return next({ path: `/${to.params.venue}/login` });
    }

    next();
  };

  orderDetailsGuard = (to, from, next) => {
    if (this[store].getters["user/getUserOrders"]) {
      return next();
    } else {
      return next({
        name: ROUTE_NAMES.ORDER_HISTORY,
        params: { venue: to.params.venue }
      });
    }
  };

  loginPageGuard = async (to, from, next) => {
    let isUserAuthorized = false;
    try {
      isUserAuthorized = await this[store].dispatch("user/checkUserIsAuthorized");
    } catch (e) {
      isUserAuthorized = false;
    }

    if (isUserAuthorized && !this[store].getters["user/isGuestUser"]) {
      return next({ path: `/${to.params.venue}` });
    }

    next();
  };

  shoppingCartPageGuard = async (to, from, next) => {
    try {
      let activeVendor = this[store].getters["vendor/getActiveVendor"];
      // Need to go re-retrieve the vendor information so we know if orderAhead is enabled
      if (activeVendor && activeVendor.id) {
        try {
          activeVendor = await this[store].dispatch("vendor/fetchVendorById", { vendorId: activeVendor.id });
        } catch (e) {
          activeVendor = this[store].getters["vendor/getActiveVendor"];
        }
      }

      const orderAheadActiveVendorId = this[store].getters["orderAhead/getActiveVendorId"];
      // If pickupTimeSelectionEnabled (orderAhead) is disabled, we need to clear the orderAhead state.
      // However, we need to keep track of the original orderAheadActiveVendorId so that the ShoppingCart
      // Page know to display a dialog indicating that orderAhead has been turned off since using the application.
      if (activeVendor && !activeVendor.pickupTimeSelectionEnabled) {
        await this[store].dispatch("orderAhead/resetState");
      }

      const pickupTime = this[store].getters["orderAhead/selectedPickup"];
      const activeVenue = this[store].getters["venue/getVenueInfo"];
      const cartItems = this[store].getters["cart/getCartItems"];
      const { timezone } = activeVenue;
      const now = DateTime.now().setZone(timezone);
      // If orderAhead is turned off, the pickupTime is null, so this will be skipped.
      if (pickupTime && mapToEndTime(pickupTime, timezone)?.startOf("minute") < now.startOf("minute")) {
        throw new Error(pastMessage);
      }

      if (!activeVendor || !cartItems.length) return next();

      //update storage with newest server configs
      try {
        await this[store].dispatch("common/updateServerConfig", this[store].getters["venue/getActiveVenue"]);
      } catch (error) {
        return next({
          name: ROUTE_NAMES.VENDOR_PICKER,
          params: {
            venue: this[store].getters["venue/getActiveVenueUrl"],
            errorMessage: error?.errorMessage || ERROR_VENDOR_SELECTED_NOT_AVAILABLE
          }
        });
      }

      await this[store].dispatch("cart/validateCart");

      // Pass the orderAheadActiveVendorId to the ShoppingCart page to determine if a dialog should be shown
      to.params.orderAheadActiveVendorId = orderAheadActiveVendorId;
      return next();
    } catch (error) {
      const openPickupModal = error?.message === pastMessage || error?.response?.status === 422;
      let errorMessage = error?.message || "Please try again.";
      if (openPickupModal) {
        errorMessage = ERROR_PICK_UP_TIME_NO_LONGER_AVAILABLE;
      }

      return next({
        name: ROUTE_NAMES.MENU,
        params: {
          ...from.params,
          openPickupModal,
          errorMessage,
          errorId: random(0, 9999999)
        }
      });
    }
  };

  validateCartBeforeCheckout = async (to, from, next) => {
    const isOAPI = this[store].getters["venueSettings/APIType"] === OAPI_API_TYPE;
    if (isOAPI) {
      try {
        const activeVenue = this[store].getters["venue/getVenueInfo"];
        await this[store].dispatch("common/updateServerConfigWithMinimumTimePassed", activeVenue?.id);
        await this[store].dispatch("cart/validateCart");
      } catch (e) {
        return next(`/not-found`);
      }
    }
    next();
  };

  checkoutPageGuard = (to, from, next) => {
    const activeVendor = this[store].getters["vendor/getActiveVendor"];
    if (!activeVendor) {
      return next(`/${to.params.venue}`);
    }
    return next();
  };

  appPageGuard = async (to, from, next) => {
    const { venue } = to.params;
    if (!venue) return next({ path: "/not-found" });

    try {
      await this.fetchVenue(venue);
      if (isNumeric(venue)) {
        return next({ path: to.path.replace(venue, this[store].getters["venue/getActiveVenueUrl"]) });
      }
      return next();
    } catch (e) {
      return next({ path: "/not-found" });
    }
  };

  venuePageGuard = (to, from, next) => {
    const activeVenue = this[store].getters["venue/getVenueInfo"];
    const seatRequired = this[store].getters["venueSettings/seatRequired"];
    const activeVendor = this[store].getters["vendor/getActiveVendor"];

    if (!activeVenue.id) {
      return next({ path: "/not-found" });
    }

    if (!seatRequired) {
      return next({ path: `/${to.params.venue}/vendors` });
    }

    return next();
  };

  /**
   *
   * @param {*} vendor
   * Fetchs vendor pickup times and selects ASAP as pickup time when auto select is enabled and user didn't select a
   * pickup time already
   */
  autoSelectPickupTime = async (vendor) => {
    if (!vendor?.pickupTimeSelectionEnabled) return;
    if (!vendor?.pickupTimeAutoSelectEnabled) return;
    if (this[store].getters["orderAhead/selectedPickup"]) return;

    await this[store].dispatch("vendor/fetchVendorPickupTimes", { vendorId: vendor.id }, { root: true });
    const pickupTimes = this[store].getters["vendor/getValidPickupTimesForTodayByVendorId"](vendor?.id);

    //will autoselect ASAP as default
    if (pickupTimes?.length) {
      this[store].dispatch("orderAhead/setSelectedPickup", { time: pickupTimes[0], id: vendor?.id });
      this[store].dispatch("vendor/setActiveDayPart", null, { root: true });
    }
  };

  vendorsPageGuard = async (to, from, next) => {
    let path = this.getRedirectPath(to.params);
    if (!path && this[store].getters["vendor/getVendorsList"].length === 1 && !to.params.errorMessage) {
      const vendor = this[store].getters["vendor/getVendorsList"][0];

      await this.autoSelectPickupTime(vendor);

      path = `${to.path}/${vendor?.id}`;
    }

    const activeVendor = this[store].getters["vendor/getActiveVendor"];

    /**
     * Sets direct access to false when user hits
     * /:venue or /:venue/vendors page directly (from.name is undefined)
     */
    if (!from?.name) {
      this[store].commit("venue/setVendorAccessedDirectly", false, { root: true });
    }

    if (activeVendor?.logoNavigation === 1 && this[store].getters["venue/wasVendorAccessedDirectly"]) {
      path = `${to.path}/${activeVendor?.id}`;
    }

    next(path);
  };

  deepLinkGuard = async (to, from, next) => {
    const payload = {
      levelName: to.params.level,
      sectionName: to.params.section,
      rowName: to.params.row,
      seatName: to.params.seat
    };

    let vendors;
    try {
      vendors = await this[store].dispatch("vendor/fetchDeepLinkVendors", payload, { root: true });
    } catch (error) {
      return next({ path: `/${to.params.venue}/` });
    }

    if (!vendors?.length) {
      return next({ path: `/${to.params.venue}/` });
    }

    this[store].commit("venue/setWasDeepLinkedUsed", true, { root: true });
    next({ path: `/${to.params.venue}/vendors` });
  };

  vendorMiddlewareGuard = async (to, from, next) => {
    const { venue, vendor } = to.params;
    if (!vendor) return next({ path: `/${venue}` });

    const activeVendor = this[store].getters["vendor/getActiveVendor"];

    /**
     * from.name is undefined (user scanned a link or refreshed page)
     * and switching vendor (or activeVendor is undefined) user did a direct access
     */
    if (!from?.name && activeVendor?.id !== +vendor) {
      this[store].commit("venue/setVendorAccessedDirectly", true, { root: true });
    }

    if (activeVendor && activeVendor.id !== +vendor) {
      try {
        this[store].dispatch("cart/clearShoppingCart");
        //If it's a different vendor, clean vendorItems state to prevent that the user see previous vendor items
        this[store].commit("vendor/resetMenuItemsState", null, { root: true });
        //update storage after new vendor is selected
        await this[store].dispatch("common/updateVenueConfig", venue, { root: true });
      } catch (error) {
        errorLoggerAdapter.createIssue(SENTRY_ERRORS.ERROR_UPDATING_SERVER_CONFIG, "routeGuards", "vendorMiddlewareGuard", {
          description: error
        });
      }
    }
    const vendorData = this[store].getters["vendor/getVendorByID"](vendor);
    try {
      await this.fetchVenue(venue);

      if (isNumeric(venue)) return next({ path: to.path.replace(venue, this[store].getters["venue/getActiveVenueUrl"]) });

      const path = this.getRedirectPath(to.params);
      // Fetch menu items in parallel (don't wait for them to show the next component)
      if (!path) {
        await this[store].dispatch("vendor/fetchVendorMenu", vendor);
        // If the target is offerings but there is no categories to show, redirect to Menu.
        const menuCategories = this[store].getters["vendor/getVendorMenu"];
        const isOAPI = this[store].getters["venueSettings/APIType"] === OAPI_API_TYPE;
        if (isOAPI && menuCategories.length === 0 && to.name === ROUTE_NAMES.OFFERINGS && from.name !== ROUTE_NAMES.MENU) {
          await this.autoSelectPickupTime(vendorData);
          return next({ name: ROUTE_NAMES.MENU, params: to.params });
        }
      }
    } catch (e) {
      return next({ path: "/not-found" });
    }
    await this.autoSelectPickupTime(vendorData);
    next();
  };

  menuPageGuard = async (to, from, next) => {
    const path = this.getRedirectPath(to.params);
    // set the menu page title
    to.meta.pageTitle = this[store].getters["vendor/getActiveVendor"].name;

    if (path) {
      return next({ path: path, params: to.params });
    }
    await this[store].dispatch("user/fetchFavoriteItemsIds");
    return next();
  };

  offeringsPageGuard = (to, from, next) => {
    if (!this[store].getters["vendor/isOfferingsPageAvailable"]) return next({ name: ROUTE_NAMES.MENU, params: to.params });

    const path = this.getRedirectPath(to.params);
    if (path) return next({ path: path });

    next();
  };

  favoritesPageGuard = async (to, from, next) => {
    const path = this.getRedirectPath(to.params);
    try {
      if (path) {
        return next(path);
      }
      await this[store].dispatch("user/fetchFavoriteItemsIds");
      return next();
    } catch (e) {
      return next(`/not-found`);
    }
  };

  orderAgainPageGuard = async (to, from, next) => {
    const path = this.getRedirectPath(to.params);
    try {
      if (path) {
        return next(path);
      }
      await this[store].dispatch("user/fetchVisitorOrders");
      return next();
    } catch (e) {
      to.params.errorOnRequest = true;
      return next({ to });
    }
  };

  orderConfirmationRouteGuard = (to, from, next) => {
    const checkoutParams = this[store].getters["cart/getCheckoutParams"];
    if (!checkoutParams?.order && !checkoutParams?.cartItems) {
      return next({ path: "/not-found" });
    } else {
      return next();
    }
  };

  resetPasswordPageGuard = async (to, from, next) => {
    try {
      await this.fetchVenue(to.query.venueID);
      return next();
    } catch (e) {
      return next(`/not-found`);
    }
  };

  saveURLTenderAndRemoveQuerys = (to, from, next) => {
    const { payment, subpayment, credential } = to.query;
    if (!payment || !subpayment || !credential) return next();

    this[store].dispatch("cart/saveURLTender", {
      paymentType: payment,
      subpaymentType: subpayment,
      credential: credential
    });

    return next({ path: to.fullPath.split("?")[0] });
  };

  removeDeepLinkedFlag = (to, from, next) => {
    // if deepLinked was used must clean venue info to get the vendor list again
    if (this[store].getters["venue/getWasDeepLinkedUsed"]) this[store].commit("venue/setVenueInfo", null, { root: true });
    this[store].commit("venue/setWasDeepLinkedUsed", null, { root: true });
    next();
  };
}

const routeGuards = new RouteGuards();

export default routeGuards;
